import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useAuthSession } from '@/features/auth/useAuthSession';
import {
  AccountLinkError,
  confirmLink,
  createLinkCode,
  fetchLinkedPlatforms,
  previewLink,
} from '@/services/accountLink';

function requireToken(accessToken: string | undefined): string {
  if (!accessToken) {
    throw new AccountLinkError('Нет активной сессии. Откройте приложение снова.', 'AUTH_ERROR');
  }
  return accessToken;
}

export function useLinkedPlatforms() {
  const { session } = useAuthSession();

  return useQuery({
    queryKey: ['linked-platforms', session?.user.id ?? null],
    queryFn: fetchLinkedPlatforms,
    enabled: Boolean(session),
  });
}

/** Код и момент истечения по часам этого устройства. */
export function useCreateLinkCode() {
  const { session } = useAuthSession();

  return useMutation({
    mutationFn: async () => {
      const { code, expiresInSeconds } = await createLinkCode(requireToken(session?.access_token));
      return { code, deadline: Date.now() + expiresInSeconds * 1000 };
    },
  });
}

export function usePreviewLink() {
  const { session } = useAuthSession();

  return useMutation({
    mutationFn: (code: string) => previewLink(requireToken(session?.access_token), code),
  });
}

/**
 * После привязки сессия этого устройства может принадлежать удалённому
 * (поглощённому) аккаунту. Как и при удалении аккаунта — не точечная
 * инвалидация, а перезапуск: AuthProvider заново спросит platform-session
 * и получит сессию главного аккаунта. forgetSession и перезапуск делает панель
 * по кнопке «Продолжить», а не здесь: пока человек читает итог, приложение
 * должно оставаться с рабочей сессией. Если закроет приложение раньше —
 * следующий вход восстановит владельца по подписи площадки (сервер решает,
 * чья сессия). Итог берётся из ответа подтверждения — состояние могло
 * измениться после предпросмотра.
 */
export function useConfirmLink() {
  const { session } = useAuthSession();

  return useMutation({
    mutationFn: (code: string) => confirmLink(requireToken(session?.access_token), code),
  });
}

export function useSecondsLeft(deadline: number | null): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (deadline === null) {
      return undefined;
    }
    // Первый отсчёт — сразу после смены deadline, дальше раз в секунду;
    // после истечения интервал останавливается.
    const tick = () => {
      const current = Date.now();
      setNow(current);
      if (current >= deadline) {
        window.clearInterval(timer);
      }
    };
    const timer = window.setInterval(tick, 1000);
    const first = window.setTimeout(tick, 0);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(first);
    };
  }, [deadline]);

  return deadline === null ? 0 : Math.max(0, Math.ceil((deadline - now) / 1000));
}

export function errorText(error: unknown): string {
  return error instanceof AccountLinkError
    ? error.message
    : 'Не удалось связаться с сервером. Попробуйте ещё раз.';
}
